import React, { useCallback, useEffect, useRef, useState } from 'react';
import SendPointer from '../../components/SendPointer';
import AttachButton from '../../components/AttachButton';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import AddressPicker from '../../components/AddressPicker';
import { openPdfFrom } from '../../utils/openPdf';
import { generateRfiPDF } from './RfiPdf';
import PlanSnip from './PlanSnip';

// The office and the field open the same job at different addresses, so a pointer is
// built from wherever the sender is standing. A supervisor sent an office link is
// redirected to their own copy (see FIELD_TWIN in App.js) rather than bounced home.
const jobPath = projectId =>
  `${window.location.pathname.startsWith('/sup/') ? '/sup/jobs' : '/admin/jobs'}/${projectId}`;

// ─────────────────────────────────────────────────────────────────────────────
// RFIs — the question you ask before the work becomes extra.
//
// Numbered per job so it prints as RFI-01, addressed by default to whoever the
// job's correspondence already goes to, and able to hand off to an Extra Work
// ticket carrying its own number. That's the point of the order on the Changes
// tile: question → answer → ticket → change order, with nothing lost between.
//
// Three states. Open means waiting on them. Answered means the reply is in — and
// the answer arriving is what sets it, rather than a status anyone has to
// remember. Closed means it needs nothing more.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS = {
  open:     { label: 'Open',     tint: '#b45309', bg: '#fffbeb' },
  answered: { label: 'Answered', tint: '#15803d', bg: '#f0fdf4' },
  closed:   { label: 'Closed',   tint: '#57534e', bg: '#f5f5f4' },
};

const lbl = { fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 };
const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff', color: '#111', fontFamily: 'inherit' };
const btn = { padding: '8px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' };

const shortDate = s => (s ? new Date(String(s).length <= 10 ? s + 'T12:00:00' : s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const rfiNo = n => `RFI-${String(n || 0).padStart(2, '0')}`;
const fileUrl = f => `${API_BASE.replace('/api', '')}/uploads/${f.filename}`;

// Days until the answer is needed — the number that decides whether to chase it.
function dueIn(rfi, today) {
  if (!rfi.needed_by || rfi.status !== 'open') return null;
  const n = Math.round((new Date(rfi.needed_by + 'T12:00:00') - new Date(today + 'T12:00:00')) / 86400000);
  if (n < 0) return { text: `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} overdue`, tint: '#b91c1c', bg: '#fef2f2' };
  if (n === 0) return { text: 'Needed today', tint: '#b45309', bg: '#fffbeb' };
  if (n <= 3) return { text: `Needed in ${n} day${n === 1 ? '' : 's'}`, tint: '#b45309', bg: '#fffbeb' };
  return null;
}

export default function RfiPillow({ projectId, project, bid, canEdit = true, compact = false, openItemId, onOpenItem }) {
  const [rfis, setRfis] = useState(null);
  const [err, setErr] = useState('');
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  // Someone was sent this exact RFI — open it rather than leaving them to find it.
  useEffect(() => { if (openItemId) setOpenId(openItemId); }, [openItemId]);
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  const load = useCallback(() => {
    apiFetch(`/projects/${projectId}/rfis`)
      .then(r => setRfis(Array.isArray(r) ? r : []))
      .catch(e => setErr(e?.error || 'Could not load the RFIs.'));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  async function create(fields) {
    try {
      const made = await apiFetch(`/projects/${projectId}/rfis`, { method: 'POST', body: JSON.stringify(fields) });
      setAdding(false);
      await load();
      setOpenId(made.id);
    } catch (e) { setErr(e?.error || 'Could not open that RFI.'); }
  }

  if (err && !rfis) return <div style={{ fontSize: 13, color: '#b91c1c' }}>{err}</div>;
  if (!rfis) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>;

  // In the pillow's window: the list, newest first, and nothing else.
  if (compact) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {rfis.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>No RFIs on this job.</div>}
        {rfis.map(r => <RfiRow key={r.id} rfi={r} today={today}
          onOpen={onOpenItem ? () => onOpenItem(r.id) : undefined} />)}
      </div>
    );
  }

  const open = rfis.find(r => r.id === openId) || null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {canEdit && !adding && (
          <button onClick={() => setAdding(true)} style={{ ...btn, background: '#c2410c', color: '#fff' }}>
            + Ask a question
          </button>
        )}
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          {rfis.length === 0 ? 'None yet' : `${rfis.filter(r => r.status === 'open').length} open of ${rfis.length}`}
        </span>
        {err && <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>{err}</span>}
      </div>

      {adding && <NewRfi project={project} onCancel={() => setAdding(false)} onCreate={create} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rfis.map(r => (
          <div key={r.id} id={`rfi-${r.id}`}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <RfiRow rfi={r} today={today}
                  onOpen={() => (onOpenItem ? onOpenItem(r.id) : setOpenId(openId === r.id ? null : r.id))} />
              </div>
              {/* This one RFI, sent to someone — they land on it, open. */}
              <SendPointer path={`${jobPath(projectId)}?pillow=rfi&item=${r.id}`}
                label={`${project?.project_number || ''} ${rfiNo(r.rfi_number)}${r.title ? ' · ' + r.title : ''}`.trim()} />
            </div>
            {open && open.id === r.id && (
              <RfiCard rfi={r} project={project} bid={bid} canEdit={canEdit} onChanged={load} onClose={() => setOpenId(null)} />
            )}
          </div>
        ))}
        {rfis.length === 0 && !adding && (
          <div style={{ fontSize: 13, color: '#9ca3af' }}>
            Nothing asked yet. An RFI here keeps the question, the answer and what it cost in one place.
          </div>
        )}
      </div>
    </div>
  );
}

function RfiRow({ rfi, today, onOpen }) {
  const st = STATUS[rfi.status] || STATUS.open;
  const due = dueIn(rfi, today);
  return (
    <button onClick={onOpen} disabled={!onOpen}
      style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit', cursor: onOpen ? 'pointer' : 'default',
        background: '#fff', border: '1px solid #ececec', borderLeft: `4px solid ${st.tint}`, borderRadius: 10, padding: '9px 11px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: '#c2410c' }}>{rfiNo(rfi.rfi_number)}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111', flex: 1, minWidth: 0 }}>{rfi.title || 'Untitled'}</span>
        <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 20, background: st.bg, color: st.tint }}>{st.label}</span>
      </div>
      {rfi.question && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{rfi.question}</div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {rfi.reference && <span style={{ fontSize: 10.5, color: '#6b7280' }}>📐 {rfi.reference}</span>}
        <span style={{ fontSize: 10.5, color: '#9ca3af' }}>asked {shortDate(rfi.asked_at)}</span>
        {due && <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 20, background: due.bg, color: due.tint }}>{due.text}</span>}
        {!!rfi.cost_impact && <span style={{ fontSize: 10, fontWeight: 800, color: '#b45309' }}>$ impact</span>}
        {!!rfi.schedule_impact && <span style={{ fontSize: 10, fontWeight: 800, color: '#1d4ed8' }}>⏱ impact</span>}
        {rfi.extra_work_id && <span style={{ fontSize: 10, fontWeight: 800, color: '#0f766e' }}>→ extra work</span>}
        {(rfi.files || []).length > 0 && <span style={{ fontSize: 10.5, color: '#9ca3af' }}>📎 {rfi.files.length}</span>}
      </div>
    </button>
  );
}

function NewRfi({ project, onCancel, onCreate }) {
  const [f, setF] = useState({ title: '', question: '', reference: '', needed_by: '' });
  const to = project?.awarded_by_name || project?.awarded_by_company;
  return (
    <div style={{ border: '1px solid #fed7aa', background: '#fff7ed', borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <div><div style={lbl}>What's it about</div>
          <input style={inp} autoFocus value={f.title} placeholder="Pole base depth"
            onChange={e => setF(v => ({ ...v, title: e.target.value }))} /></div>
        <div><div style={lbl}>Drawing / spec</div>
          <input style={inp} value={f.reference} placeholder="E-4 / detail 3"
            onChange={e => setF(v => ({ ...v, reference: e.target.value }))} /></div>
        <div><div style={lbl}>Answer needed by</div>
          <input style={inp} type="date" value={f.needed_by}
            onChange={e => setF(v => ({ ...v, needed_by: e.target.value }))} /></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={lbl}>The question</div>
        <textarea style={{ ...inp, minHeight: 74, resize: 'vertical', fontFamily: 'inherit' }} value={f.question}
          placeholder="What do you need them to tell you?"
          onChange={e => setF(v => ({ ...v, question: e.target.value }))} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => f.question.trim() && onCreate(f)} disabled={!f.question.trim()}
          style={{ ...btn, background: f.question.trim() ? '#c2410c' : '#e5e7eb', color: f.question.trim() ? '#fff' : '#9ca3af' }}>
          Open the RFI
        </button>
        <button onClick={onCancel} style={{ ...btn, background: '#fff', border: '1px solid #e5e7eb', color: '#374151' }}>Cancel</button>
        <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
          Goes to {to || 'the job’s general contact'}, numbered automatically.
        </span>
      </div>
    </div>
  );
}

function RfiCard({ rfi, project, bid, canEdit, onChanged, onClose }) {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const shown = (k, fallback) => (k in draft ? draft[k] : fallback);
  const st = STATUS[rfi.status] || STATUS.open;
  // Emailing the RFI: whether the server can send (and as whom), and the open composer.
  const [mail, setMail] = useState(null);
  const [email, setEmail] = useState(null);   // { to, cc, subject, message, busy, err }
  const [snip, setSnip] = useState(null);     // 'question' | 'answer' while the plan-snip window is open
  useEffect(() => { apiFetch('/mail/status').then(setMail).catch(() => setMail({ configured: false })); }, []);
  // The ticket this RFI became (if it did): change order yet? in Fieldwire yet?
  const [tk, setTk] = useState(null);
  const loadTicket = () => { if (rfi.extra_work_id) apiFetch(`/rfis/${rfi.id}/ticket`).then(setTk).catch(() => setTk(null)); else setTk(null); };
  useEffect(loadTicket, [rfi.id, rfi.extra_work_id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function makeChangeOrder() {
    const t = tk?.ticket; if (!t) return;
    if (!window.confirm(`Make CO-${String(t.co_number).padStart(2, '0')} a change order?\n\nIt moves onto the job's Change Orders and can be billed.`)) return;
    setBusy('co'); setErr('');
    try { await apiFetch(`/sub-jobs/${t.sub_job_id}/extra-work/${t.id}/promote`, { method: 'POST' }); loadTicket(); await onChanged(); }
    catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setBusy('');
  }
  async function pinInFieldwire() {
    const t = tk?.ticket; if (!t) return;
    setBusy('pin'); setErr('');
    try { await apiFetch(`/sub-jobs/${t.sub_job_id}/extra-work/${t.id}/fieldwire/pin`, { method: 'POST', body: JSON.stringify({}) }); loadTicket(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not pin it.'); }
    setBusy('');
  }
  async function sendToFieldwire() {
    const t = tk?.ticket; if (!t) return;
    if (!window.confirm(`Send CO-${String(t.co_number).padStart(2, '0')} to Fieldwire?\n\nA task goes in under Extra Work with the question and answer as its first comment and the RFI's photos attached. It's left unassigned — the PM assigns it in Fieldwire.`)) return;
    setBusy('fw'); setErr('');
    try { const r = await apiFetch(`/sub-jobs/${t.sub_job_id}/extra-work/${t.id}/fieldwire`, { method: 'POST', body: JSON.stringify({}) }); loadTicket(); await onChanged(); window.alert(`In Fieldwire as task #${r.task?.number || '?'}${r.task?.team ? ' under ' + r.task.team : ''}${r.task?.photos ? `, with ${r.task.photos} photo${r.task.photos === 1 ? '' : 's'}` : ''}.`); }
    catch (e) { setErr(e?.error || e?.message || 'Fieldwire did not take it.'); }
    setBusy('');
  }

  // The card is full screen: the form on the left, and on the right the RFI exactly as it
  // prints — the server-drawn PDF on a letter-size (8.5×11) page, redrawn after every save
  // or attachment (Ryan, Sep 16: "show what the RFI will look like"). Narrow screens get
  // the form and a Preview button instead of the side-by-side.
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => { const f = () => setVp({ w: window.innerWidth, h: window.innerHeight }); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  const wide = vp.w >= 1100;
  // The paper's size in pixels: as tall as the window allows, never wider than the space
  // beside (or under) the form. The PDF viewer inside is drawn at a fixed 850×1100 — a
  // letter page at 100% with a little margin — and the whole frame is scaled to fit, because
  // the browser's viewer doesn't reliably honour a "fit page" request inside a frame.
  const paperW = Math.max(260, Math.min((vp.h - 110) * 8.5 / 11, wide ? vp.w - 600 - 60 : vp.w - 80));
  const paperH = paperW * 11 / 8.5;
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewState, setPreviewState] = useState('idle');   // idle | drawing | ready | failed
  const previewKey = JSON.stringify([rfi.title, rfi.reference, rfi.asked_to, rfi.asked_at, rfi.needed_by, rfi.question, rfi.answer, rfi.answered_at, rfi.status, rfi.cost_impact, rfi.schedule_impact, (rfi.files || []).map(f => f.id)]);
  const previewUrlRef = useRef(null);
  useEffect(() => {
    if (!wide && !showPreview) return undefined;
    let live = true;
    setPreviewState('drawing');
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/projects/${rfi.project_id}/rfis/${rfi.id}/pdf?preview=1`, { method: 'POST', headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json' }, body: '{}' });
        if (!r.ok) throw new Error('pdf');
        const url = URL.createObjectURL(await r.blob());
        if (!live) { URL.revokeObjectURL(url); return; }
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url; setPreviewUrl(url); setPreviewState('ready');
      } catch { if (live) setPreviewState('failed'); }
    }, 500);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, rfi.id, wide, showPreview]);
  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current); }, []);
  useEffect(() => { const k = e => { if (e.key === 'Escape' && !snip && !email) onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [snip, email, onClose]);

  // The PDF is drawn on the server and filed on the Documents tile; the browser print
  // window is only the fallback if the server can't draw it.
  async function exportPdf() {
    const win = window.open('', '_blank');
    try {
      const r = await apiFetch(`/projects/${rfi.project_id}/rfis/${rfi.id}/pdf`, { method: 'POST', body: JSON.stringify({}) });
      const url = `${API_BASE}/documents/${r.document.id}/file`;
      if (win) win.location.href = url; else window.open(url, '_blank');
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId: rfi.project_id } }));
    } catch { if (win) win.close(); generateRfiPDF(rfi, project, bid); }
  }
  function openEmail() {
    const who = rfi.asked_to || (project?.awarded_by_company && project?.awarded_by_name !== project?.awarded_by_company ? project?.awarded_by_name : '') || '';
    const first = who ? String(who).trim().split(/\s+/)[0] : '';
    const jobName = String(project?.name || '').startsWith(String(project?.project_number || ' ')) ? (project?.name || '') : `${project?.project_number || ''} ${project?.name || ''}`;
    const needed = rfi.needed_by ? new Date(rfi.needed_by + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : null;
    setEmail({ to: project?.awarded_by_email || '', cc: '',
      subject: `${rfiNo(rfi.rfi_number)}${rfi.title ? ' — ' + rfi.title : ''} · ${jobName}`.replace(/\s+/g, ' ').trim(),
      message: `Hello${first ? ' ' + first : ''},\n\nPlease see the attached ${rfiNo(rfi.rfi_number)}${rfi.title ? ' regarding ' + rfi.title : ''} on ${project?.project_number || 'the job'}.${needed ? ` We need an answer by ${needed} to stay on schedule.` : ''}\n\nThank you,`,
      busy: false, err: '' });
  }
  async function sendEmail() {
    if (!email || email.busy) return;
    setEmail(e => ({ ...e, busy: true, err: '' }));
    try {
      const r = await apiFetch(`/projects/${rfi.project_id}/rfis/${rfi.id}/email`, { method: 'POST', body: JSON.stringify({ to: email.to, cc: email.cc, subject: email.subject, message: email.message }) });
      window.dispatchEvent(new CustomEvent('titan:documents-changed', { detail: { projectId: rfi.project_id } }));
      setEmail(null); setErr('');
      await onChanged();
      window.alert(`${rfiNo(rfi.rfi_number)} emailed to ${email.to}${r.from ? ' from ' + r.from : ''}${r.attachments > 1 ? ` with ${r.attachments - 1} attachment${r.attachments > 2 ? 's' : ''}` : ''}.${r.warning ? '\n\n' + r.warning : ''}`);
    } catch (e) { setEmail(x => x ? { ...x, busy: false, err: e?.error || e?.message || 'The email didn’t go.' } : x); }
  }

  async function save(patch) {
    setBusy('save'); setErr('');
    try { await apiFetch(`/rfis/${rfi.id}`, { method: 'PATCH', body: JSON.stringify(patch) }); await onChanged(); }
    catch (e) { setErr(e?.error || 'Could not save that.'); }
    setBusy('');
  }

  async function upload(side, file, spot) {
    if (!file) return;
    setBusy('file'); setErr('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('side', side);
    if (spot) fd.append('spot', JSON.stringify(spot));   // which sheet, and where — so a change order can be pinned there
    try {
      const r = await fetch(`${API_BASE}/rfis/${rfi.id}/files`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      if (!r.ok) throw new Error();
      await onChanged();
    } catch { setErr('That file did not upload.'); }
    setBusy('');
  }

  async function removeFile(f) {
    if (!window.confirm(`Remove ${f.original_name}?`)) return;
    try { await apiFetch(`/rfis/${rfi.id}/files/${f.id}`, { method: 'DELETE' }); await onChanged(); }
    catch (e) { setErr(e?.error || 'Could not remove that.'); }
  }

  async function toExtraWork() {
    if (!window.confirm(`Turn ${rfiNo(rfi.rfi_number)} into an Extra Work ticket?\n\nThe ticket opens carrying this question and its number.`)) return;
    setBusy('ew'); setErr('');
    try {
      const r = await apiFetch(`/rfis/${rfi.id}/extra-work`, { method: 'POST' });
      await onChanged();
      window.alert(`Made CO-${String(r.ticket.co_number).padStart(2, '0')} on the Extra Work pillow.`);
    } catch (e) { setErr(e?.error || 'Could not make the ticket.'); }
    setBusy('');
  }

  async function remove() {
    if (!window.confirm(`Delete ${rfiNo(rfi.rfi_number)}? This can't be undone.`)) return;
    try { await apiFetch(`/rfis/${rfi.id}`, { method: 'DELETE' }); await onChanged(); onClose(); }
    catch (e) { setErr(e?.error || 'Could not delete that.'); }
  }

  const files = side => (rfi.files || []).filter(f => f.side === side);
  // The question and answer boxes grow with what's typed (up to a point), so a ten-line
  // question isn't read through a three-line slot.
  const grow = v => Math.max(70, Math.min(300, String(v || '').split(/\n/).length * 19 + 24));

  const Files = ({ side, multiple = false, label = 'Attach', snipToo = true }) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
      {files(side).map(f => (
        <span key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, background: '#fff',
          border: '1px solid #e5e7eb', borderRadius: 7, padding: '4px 8px' }}>
          <a href={fileUrl(f)} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontWeight: 700 }}>
            {/^image\//.test(f.mime_type || '') ? '🖼' : '📄'} {f.original_name}
          </a>
          {canEdit && <button onClick={() => removeFile(f)} title="Remove"
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 0, fontSize: 13 }}>×</button>}
        </span>
      ))}
      {canEdit && (
        // Compact but drop-capable — drag a file onto it, or click to browse.
        <AttachButton accept="any" icon="📎" variant="plain" label={label} multiple={multiple}
          style={{ padding: '4px 10px', fontSize: 11.5, borderRadius: 7, fontWeight: 700, color: '#c2410c' }}
          onFiles={async fs => { for (const f of (multiple ? fs : fs.slice(0, 1))) await upload(side, f); }} />
      )}
      {canEdit && snipToo && (
        // Open a Fieldwire plan sheet, zoom to the problem, ring it, and attach the picture.
        <button onClick={() => setSnip(side)} title="Open the job's plan sheets, zoom to the problem and attach a picture of it"
          style={{ padding: '4px 10px', fontSize: 11.5, borderRadius: 7, fontWeight: 700, color: '#1e3a5f', background: '#fff', border: '1px solid #e5e7eb', cursor: 'pointer' }}>📐 Snip from plans</button>
      )}
    </div>
  );

  // The paper: a letter-size frame that fills the height, showing the drawn PDF (two pages
  // scroll inside it when the RFI runs long).
  const Paper = () => (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: paperW }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.4 }}>The RFI as it prints</span>
        <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)' }}>{previewState === 'drawing' ? 'redrawing…' : previewState === 'failed' ? 'could not draw it' : 'letter · 8.5 × 11'}</span>
        <span style={{ flex: 1 }} />
        {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: '#fff', fontWeight: 700 }}>Open full size ↗</a>}
      </div>
      <div style={{ width: paperW, height: paperH, flexShrink: 0, background: '#fff', borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.45)', overflow: 'hidden', position: 'relative' }}>
        {previewUrl && <iframe title="RFI preview" src={`${previewUrl}#zoom=100&toolbar=0&navpanes=0`}
          style={{ width: 850, height: 1100, border: 0, display: 'block', transform: `scale(${paperW / 850})`, transformOrigin: '0 0' }} />}
        {!previewUrl && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>{previewState === 'failed' ? 'The PDF could not be drawn.' : 'Drawing the RFI…'}</div>}
      </div>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 6000, background: 'rgba(15,23,42,0.72)', display: 'flex', gap: 14, padding: '12px 14px', alignItems: 'stretch' }}>
      {/* Snip from plans: a Fieldwire sheet or photo, zoomed to the problem, captured as a PNG on this RFI. */}
      {snip && (
        <PlanSnip projectId={rfi.project_id} title={`${rfiNo(rfi.rfi_number)} · snip from the plans`} onClose={() => setSnip(null)}
          onCapture={(blob, name, src, spot) => upload(snip, new File([blob], name, { type: 'image/png' }), spot)} />
      )}
      <div onClick={e => e.stopPropagation()} style={{ width: wide ? 600 : '100%', maxWidth: '100%', flexShrink: 0, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, background: '#fbfbfa' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1, padding: 0 }}>‹</button>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#c2410c' }}>{rfiNo(rfi.rfi_number)}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#111', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rfi.title || 'Untitled'}</span>
        {/* Rename from the header — the "What it's about" box below does the same, but the
            PM and Executive shouldn't have to find it (2624, Sep 2026). */}
        {canEdit && (
          <button onClick={() => { const v = window.prompt('Rename this RFI:', rfi.title || ''); if (v != null && v.trim() !== (rfi.title || '')) save({ title: v.trim() }); }} title="Rename this RFI"
            style={{ ...btn, background: '#fff', border: '1px solid #e5e7eb', color: '#6b7280', padding: '4px 9px', fontSize: 11.5 }}>✎ Rename</button>
        )}
        <select value={rfi.status} disabled={!canEdit} onChange={e => save({ status: e.target.value })}
          style={{ ...inp, width: 'auto', cursor: 'pointer', fontWeight: 700, background: st.bg, color: st.tint, borderColor: st.tint + '55' }}>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        {!wide && (
          <button onClick={() => setShowPreview(v => !v)} style={{ ...btn, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>{showPreview ? 'Hide preview' : '👁 Preview'}</button>
        )}
        <button onClick={onClose} title="Close (Esc)" style={{ ...btn, background: '#fff', border: '1px solid #e5e7eb', color: '#6b7280', padding: '4px 10px', fontSize: 15, lineHeight: 1, order: 99 }}>×</button>
        <button onClick={exportPdf}
          style={{ ...btn, background: '#1e3a5f', color: '#fff' }}>⬇ Export RFI PDF</button>
        {canEdit && (
          <button onClick={() => mail?.configured ? (email ? setEmail(null) : openEmail()) : setErr('Email isn’t set up on the server yet.')}
            title={mail?.configured ? `Sends from ${mail.from}` : 'Email isn’t set up on the server yet'}
            style={{ ...btn, background: mail?.configured ? '#15803d' : '#e5e7eb', color: mail?.configured ? '#fff' : '#9ca3af' }}>✉️ Email RFI</button>
        )}
        {canEdit && !rfi.extra_work_id && (
          <button onClick={toExtraWork} disabled={busy === 'ew'}
            style={{ ...btn, background: '#065f46', color: '#fff' }}>→ Make Extra Work ticket</button>
        )}
        {email && (
          <div style={{ flexBasis: '100%', border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.4 }}>Email this RFI — from {mail?.from || 'the company mailbox'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <AddressPicker inputStyle={inp} value={email.to} onChange={v => setEmail(x => ({ ...x, to: v }))} placeholder="To — who answers this; pick from the job or type" projectId={rfi.project_id} />
              <AddressPicker inputStyle={inp} value={email.cc} onChange={v => setEmail(x => ({ ...x, cc: v }))} placeholder="Cc (optional)" projectId={rfi.project_id} />
            </div>
            <input style={inp} value={email.subject} onChange={e => setEmail(x => ({ ...x, subject: e.target.value }))} placeholder="Subject" />
            <textarea style={{ ...inp, minHeight: 90, resize: 'vertical' }} value={email.message} onChange={e => setEmail(x => ({ ...x, message: e.target.value }))} />
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>Attached: the RFI as one combined PDF — pictures print in it and attached PDFs are merged behind it{(rfi.files || []).length ? ` (${(rfi.files || []).length} file${(rfi.files || []).length === 1 ? '' : 's'} on this RFI)` : ''}. Filed on the Documents tile.</div>
            {email.err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700 }}>{email.err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEmail(null)} disabled={email.busy} style={{ ...btn, background: '#fff', border: '1px solid #e5e7eb', color: '#374151' }}>Cancel</button>
              {/* The RFI PDF as it will be attached — drawn, not filed. */}
              <button onClick={() => openPdfFrom(`/projects/${rfi.project_id}/rfis/${rfi.id}/pdf?preview=1`, { method: 'POST', body: {} }).catch(e => setEmail(x => x ? { ...x, err: e?.message || 'Could not open the preview.' } : x))}
                disabled={email.busy} title="Open the RFI PDF that will be attached" style={{ ...btn, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>👁 Preview PDF</button>
              <button onClick={sendEmail} disabled={email.busy || !email.to.trim()}
                style={{ ...btn, background: email.to.trim() ? '#15803d' : '#cbd5e1', color: '#fff' }}>{email.busy ? 'Sending…' : '✉️ Send RFI'}</button>
            </div>
          </div>
        )}
        {rfi.extra_work_id && (() => {
          const t = tk?.ticket; const co = t ? `CO-${String(t.co_number).padStart(2, '0')}` : 'the ticket';
          const fwTask = tk?.fieldwire?.task;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <a href={`/admin/new-projects/${rfi.project_id}?pillow=${t?.promoted_at ? 'change_orders' : 'extra_work'}`} onClick={e => e.stopPropagation()}
                style={{ fontSize: 11.5, fontWeight: 700, color: '#0f766e', textDecoration: 'underline' }}>
                {t?.promoted_at ? `Change order ${co} ›` : `Extra Work ticket ${co} ›`}
              </a>
              {canEdit && t && !t.promoted_at && (
                <button onClick={makeChangeOrder} disabled={busy === 'co'} style={{ ...btn, background: '#7c2d12', color: '#fff' }}>→ Make it a Change Order</button>
              )}
              {canEdit && t?.promoted_at && tk?.fieldwire?.linked && !fwTask && (
                <button onClick={sendToFieldwire} disabled={busy === 'fw'} style={{ ...btn, background: '#1d4ed8', color: '#fff' }}>{busy === 'fw' ? 'Sending…' : '→ Send to Fieldwire'}</button>
              )}
              {fwTask && (
                <a href={fwTask.openUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title="Open this task in Fieldwire"
                  style={{ fontSize: 11.5, fontWeight: 700, color: '#1d4ed8', textDecoration: 'underline' }}>Open {fwTask.number ? 'task #' + fwTask.number : 'the task'} in Fieldwire{fwTask.pinned ? ' (on the sheet)' : ''} ↗</a>
              )}
              {canEdit && fwTask && !fwTask.pinned && tk?.spot && (
                <button onClick={pinInFieldwire} disabled={busy === 'pin'} style={{ ...btn, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>{busy === 'pin' ? 'Pinning…' : '📍 Pin it on the sheet'}</button>
              )}
              {canEdit && fwTask && !fwTask.pinned && !tk?.spot && <span style={{ fontSize: 11, color: '#9ca3af' }}>(snip the spot from a plan sheet to pin it)</span>}
              {t?.promoted_at && tk && !tk.fieldwire?.linked && <span style={{ fontSize: 11, color: '#9ca3af' }}>(job isn’t linked to Fieldwire)</span>}
            </span>
          );
        })()}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div><div style={lbl}>What it's about</div>
          <input style={inp} disabled={!canEdit} value={shown('title', rfi.title || '')}
            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            onBlur={e => e.target.value !== (rfi.title || '') && save({ title: e.target.value })} /></div>
        <div><div style={lbl}>Drawing / spec</div>
          <input style={inp} disabled={!canEdit} value={shown('reference', rfi.reference || '')}
            onChange={e => setDraft(d => ({ ...d, reference: e.target.value }))}
            onBlur={e => e.target.value !== (rfi.reference || '') && save({ reference: e.target.value })} /></div>
        <div><div style={lbl}>Asked of</div>
          <input style={inp} disabled={!canEdit} value={shown('asked_to', rfi.asked_to || '')}
            onChange={e => setDraft(d => ({ ...d, asked_to: e.target.value }))}
            onBlur={e => e.target.value !== (rfi.asked_to || '') && save({ asked_to: e.target.value })} /></div>
        <div><div style={lbl}>Asked</div>
          <input style={inp} type="date" disabled={!canEdit} value={shown('asked_at', (rfi.asked_at || '').slice(0, 10))}
            onChange={e => setDraft(d => ({ ...d, asked_at: e.target.value }))}
            onBlur={e => save({ asked_at: e.target.value })} /></div>
        <div><div style={lbl}>Answer needed by</div>
          <input style={inp} type="date" disabled={!canEdit} value={shown('needed_by', rfi.needed_by || '')}
            onChange={e => setDraft(d => ({ ...d, needed_by: e.target.value }))}
            onBlur={e => save({ needed_by: e.target.value })} /></div>
      </div>

      {/* What kind of RFI this is — above the question, where it's decided. Flagged here so
          it's on the record before anyone argues about it later; "Information only" says
          plainly that no money or time is being asked for (ticking it clears the other two). */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {[['cost_impact', 'Affects cost', '#b45309'], ['schedule_impact', 'Affects the schedule', '#b45309'], ['info_only', 'Information only', '#1d4ed8']].map(([k, text, tint]) => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700,
            color: rfi[k] ? tint : '#6b7280', cursor: canEdit ? 'pointer' : 'default' }}>
            <input type="checkbox" checked={!!rfi[k]} disabled={!canEdit}
              onChange={e => save({ [k]: e.target.checked })}
              style={{ width: 15, height: 15, accentColor: k === 'info_only' ? '#1d4ed8' : '#c2410c' }} />
            {text}
          </label>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={lbl}>The question</div>
        <textarea style={{ ...inp, minHeight: grow(shown('question', rfi.question || '')), resize: 'vertical', fontFamily: 'inherit' }} disabled={!canEdit}
          value={shown('question', rfi.question || '')}
          onChange={e => setDraft(d => ({ ...d, question: e.target.value }))}
          onBlur={e => e.target.value !== (rfi.question || '') && save({ question: e.target.value })} />
        <Files side="question" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={lbl}>The answer{rfi.answered_at ? ` · came back ${shortDate(rfi.answered_at)}` : ''}</div>
        <textarea style={{ ...inp, minHeight: grow(shown('answer', rfi.answer || '')), resize: 'vertical', fontFamily: 'inherit' }} disabled={!canEdit}
          placeholder="Type what they came back with — that's what marks it answered."
          value={shown('answer', rfi.answer || '')}
          onChange={e => setDraft(d => ({ ...d, answer: e.target.value }))}
          onBlur={e => e.target.value !== (rfi.answer || '') && save({ answer: e.target.value })} />
        <Files side="answer" />
      </div>

      {/* Behind the RFI: drop PDFs or pictures here and they're merged in after the last
          page, so the RFI publishes, prints and emails as ONE combined PDF. */}
      <div style={{ marginBottom: 12, border: '1.5px dashed #cbd5e1', borderRadius: 10, padding: '10px 12px', background: '#fff' }}>
        <div style={lbl}>Attach behind the RFI · one combined PDF</div>
        <div style={{ fontSize: 11.5, color: '#6b7280', margin: '2px 0 2px' }}>
          Drop PDFs or pictures here — spec pages, cut sheets, the marked-up drawing. They print after the RFI, in this order. PDFs attached to the question or answer are merged in too; Word and Excel files can't be and go as separate files.
        </div>
        <Files side="behind" multiple label="Drop or choose files" snipToo={false} />
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ flex: 1 }} />
        {err && <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>{err}</span>}
        {busy && <span style={{ fontSize: 12, color: '#9ca3af' }}>Saving…</span>}
        {canEdit && (
          <button onClick={remove}
            style={{ ...btn, background: '#fff', border: '1px solid #fecaca', color: '#b91c1c' }}>Delete</button>
        )}
      </div>
      {/* Narrow screens: the paper under the form, on request. */}
      {!wide && showPreview && (
        <div style={{ marginTop: 14, background: '#1e3a5f', borderRadius: 10, padding: 10, display: 'flex' }}><Paper /></div>
      )}
      </div>
      {wide && <div onClick={e => e.stopPropagation()} style={{ flex: 1, minWidth: 0, display: 'flex' }}><Paper /></div>}
    </div>
  );
}
